<template>
  <div class="clinic-calendar-widget">
    <section v-if="store.connection" class="w-connection" role="status" aria-live="polite">
      <p>{{ store.connection.message }}</p>
      <button v-if="store.connection.retry" type="button" @click="store.retry()">
        Повторить подключение
      </button>
    </section>
    <div
      v-show="!store.connection"
      ref="root"
      class="w-root"
      :data-narrow="String(store.narrow)"
      :data-condensed="String(store.condensed)"
      :aria-busy="String(store.loading || store.mutating)"
      :inert="store.mutating"
      @click.capture="guardBusy"
      @change.capture="guardBusy"
      @keydown.esc="store.closeRoomMenu()"
    >
      <template v-if="store.initialized">
        <CalendarHeader />
        <BranchSchedule v-if="store.screen === 'branch'" />
        <CalendarScreen v-else />
        <div class="w-toast" aria-live="polite" :role="toastRole">
          <template v-if="store.toast">{{ store.toast.text }}</template>
        </div>
        <CalendarFooter />
        <RoomMenu :container="root" />
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  computed, onBeforeUnmount, onMounted, ref, watch,
} from 'vue';
import { useScheduleStore } from '@/store/calendar/scheduleModule';
import type { CalendarContext, CalendarRequest } from '@/store/calendar/types';
import CalendarHeader from './calendar/CalendarHeader.vue';
import CalendarScreen from './calendar/CalendarScreen.vue';
import BranchSchedule from './calendar/BranchSchedule.vue';
import CalendarFooter from './calendar/CalendarFooter.vue';
import RoomMenu from './calendar/RoomMenu.vue';
import './calendar/calendar.css';

type CalendarProps = {
  context: CalendarContext;
  request: CalendarRequest;
  refreshRevision?: number;
};

type CalendarEvents = {
  (event: 'event', data: { event: string; payload: Record<string, unknown> }): void;
};

const props = defineProps<CalendarProps>();
const emit = defineEmits<CalendarEvents>();

const store = useScheduleStore();
const root = ref<HTMLElement | null>(null);
let observer: ResizeObserver | null = null;

const toastRole = computed(() => (store.toast?.ok === false ? 'alert' : 'status'));

const refresh = () => store.refresh();
const setContext = (context: CalendarContext) => store.setContext(context);

// Пока календарь загружается или сохраняет, действия игнорируются — кроме «Обновить»
const guardBusy = (event: Event) => {
  const target = event.target as HTMLElement;
  if (!store.busy || target.closest('[data-action="refresh"]')) return;
  if (event.type === 'click' && !target.closest('button')) return;
  event.stopPropagation();
  event.preventDefault();
};

// Ширина решает раскладку: узкая — один день, средняя — сокращённые подписи
const updateWidth = () => {
  const width = root.value?.clientWidth || 0;
  if (!width || width === store.layoutWidth) return;
  store.layoutWidth = width;
  store.closeRoomMenu();
};

// Перерисовка меняет высоту корня; если сделать её прямо в колбэке наблюдателя,
// браузер ругается «ResizeObserver loop completed…». Откладываем на следующий кадр
let frameId = 0;
const scheduleWidthUpdate = () => {
  cancelAnimationFrame(frameId);
  frameId = requestAnimationFrame(updateWidth);
};

onMounted(() => {
  store.connect(
    props.request,
    (event, payload) => emit('event', { event, payload }),
  );
  if (root.value && typeof ResizeObserver !== 'undefined') {
    observer = new ResizeObserver(scheduleWidthUpdate);
    observer.observe(root.value);
  }
  store.setContext(props.context);
});

watch(() => props.context, setContext, { deep: true });
watch(() => props.refreshRevision, refresh);

onBeforeUnmount(() => {
  observer?.disconnect();
  cancelAnimationFrame(frameId);
  store.disconnect();
});

defineExpose({ refresh, setContext });
</script>
